// Shared orchestration for the AI assistant surfaces (Drawer panel + /ai page).
// Owns the chat state machine wiring, conversation history, composer draft,
// command-palette open state, and the new/rename/delete handlers — so the two
// layouts that consume it stay thin and never duplicate this logic.

import { useCallback, useEffect, useState } from 'react';
import { Modal } from 'antd';
import { useAgentChat } from './useAgentChat';
import { usePageContext } from './usePageContext';
import { agentAPI } from '../services/agentApi';
import type { AgentConversationSummary, PageContext } from '../types/agent';

export interface AgentChatPanel {
  items: ReturnType<typeof useAgentChat>['items'];
  streaming: boolean;
  conversationId: number | null;
  conversations: AgentConversationSummary[];
  pageContext: PageContext;
  draft: string;
  setDraft: (v: string) => void;
  paletteOpen: boolean;
  setPaletteOpen: (v: boolean) => void;
  sendText: (text: string) => Promise<void>;
  handleSend: () => void;
  handleSelectConversation: (id: number) => Promise<void>;
  handleNewChat: () => void;
  handleRename: (id: number, title: string) => Promise<void>;
  handleDelete: (id: number) => Promise<void>;
  confirmProposal: (itemId: string) => Promise<void>;
  allowAlways: (itemId: string) => Promise<void>;
}

/**
 * @param active whether this surface is currently shown — gates history refresh
 *   and the Cmd-K binding so a hidden Drawer doesn't grab the shortcut.
 */
export function useAgentChatPanel(active: boolean): AgentChatPanel {
  const {
    items,
    streaming,
    conversationId,
    sendMessage,
    confirmProposal,
    allowAlways,
    loadConversation,
    reset,
  } = useAgentChat();
  const pageContext = usePageContext();
  const [draft, setDraft] = useState('');
  const [conversations, setConversations] = useState<AgentConversationSummary[]>([]);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const refreshHistory = useCallback(async () => {
    try {
      const rows = await agentAPI.listConversations();
      setConversations(Array.isArray(rows) ? rows : []);
    } catch {
      setConversations([]);
    }
  }, []);

  useEffect(() => {
    if (active) refreshHistory();
  }, [active, refreshHistory]);

  // NOTE: Cmd/Ctrl-K is owned by the global command palette in AdminLayout.
  // The assistant's own palette is opened via a button (setPaletteOpen) to
  // avoid a second handler firing on the same shortcut.

  const sendText = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;
      setDraft('');
      await sendMessage(trimmed, pageContext);
      refreshHistory();
    },
    [streaming, sendMessage, pageContext, refreshHistory],
  );

  const handleSend = useCallback(() => {
    void sendText(draft);
  }, [sendText, draft]);

  const handleSelectConversation = useCallback(
    async (id: number) => {
      await loadConversation(id);
    },
    [loadConversation],
  );

  const handleNewChat = useCallback(() => {
    if (items.length === 0) {
      reset();
      return;
    }
    Modal.confirm({
      title: 'Почати новий чат?',
      content: 'Поточну розмову буде очищено з екрана (вона залишиться в історії).',
      okText: 'Новий чат',
      cancelText: 'Скасувати',
      onOk: () => reset(),
    });
  }, [items.length, reset]);

  const handleRename = useCallback(
    async (id: number, title: string) => {
      try {
        await agentAPI.renameConversation(id, title);
      } finally {
        refreshHistory();
      }
    },
    [refreshHistory],
  );

  const handleDelete = useCallback(
    async (id: number) => {
      try {
        await agentAPI.deleteConversation(id);
        if (id === conversationId) reset();
      } finally {
        refreshHistory();
      }
    },
    [conversationId, reset, refreshHistory],
  );

  return {
    items,
    streaming,
    conversationId,
    conversations,
    pageContext,
    draft,
    setDraft,
    paletteOpen,
    setPaletteOpen,
    sendText,
    handleSend,
    handleSelectConversation,
    handleNewChat,
    handleRename,
    handleDelete,
    confirmProposal,
    allowAlways,
  };
}
